// Story 8.5 (ADO-2014), D-8: the cut-off pill (Figma "CutOffPill", Size = Compact / Small /
// Large, Urgency = Critical / Soon / Later). Advisory only: it never blocks anything.
//
// Usage (one basket hook feeds it; the read is shared, so a new mount costs nothing):
//
//   const cutOff = useDepotCutOff(depotId);          // apps/web/src/basket
//   <CutOffPill {...cutOff} />                       // header chip, depot card
//   <CutOffPill {...cutOff} size="large" />          // CutOffBanner
//
// The pill stays presentational (plain props, no fetching), so specs and the a11y suite
// render it directly.
//
// Props:
//   - `status`: `null` (loading, failed, no depot) and `{ state: 'NONE' }` (no cut-off at
//     this depot, AC3/AC4) both render NOTHING.
//   - `countdown`: from `useDepotCutOff`; nothing renders until it has one.
//   - `size` (default `small`): `compact` (20px), `small` (24px) or `large` (34px), the
//     Figma sizes (story D-16). Small is the header chip, the depot card header and the
//     cart panel's cut-off row; large is the cart banner.
//   - `phoneSize`: the size below 768px (the design system's small-screen
//     boundary), applied in CSS. Figma: compact for the header chip and the panel row,
//     small for the banner; the depot card stays small, so it passes none.
//   - `enableFinalSecondsPulse` (default `true`): pulse while 10 seconds or fewer are left
//     ("0m 10s" to "0m 00s", until the refetch brings the next deadline). The 10 seconds
//     are the pill's own rule; pass `false` to opt out. Reduced motion never pulses.
//
// OPEN and PASSED look the same (story D-14): PASSED counts down to `nextDeadline`, so when
// a cut-off passes the pill goes from red "0m 00s" to the next countdown in its own tone.
// PASSED without a next deadline renders nothing.
//
// `CutOffAnnouncer` is the separate, visually hidden polite live region that speaks at
// most once for each of 60, 30 and 10 minutes left and at passing (UX spec :1449). After a
// jump across several thresholds (a throttled or backgrounded tab), only the most urgent
// one crossed is spoken, so it never announces a time that has already gone by. It compares
// readings for one deadline only (`countdownDeadline`): a depot switch or the refetch moving
// on to the next cut-off starts afresh and says nothing for the jump. Each announcement is
// a new node in the region, so the same text twice in a row ("Cut-off passed" for two
// cut-offs under 10 minutes apart) is spoken twice. Mount ONE per screen (the header does),
// outside any button: a button's children are presentational.
//
// Copy and tone come from the seconds left, rounded up (story D-13 and D-17): `urgent`
// under 10 min ("9m 59s" down), `warning` at or under 60 min ("60m 00s" to "10m 00s"),
// both ticking, else `calm` ("1h 1m", "24h 0m", "3d 0h"), for OPEN and PASSED alike. The
// pulse and the announcer read the same rounded-up value, so "Cut-off in 60 minutes" is
// spoken as the pill shows "60m 00s" and "Cut-off passed" as it shows "0m 00s".
// a11y: `role="timer"` with the full reading as its accessible name.

import { useEffect, useRef, useState } from 'react';
import {
  countdownDeadline,
  countdownLabel,
  formatCountdown,
  isCutOffShown,
  type CutOffDisplayStatus,
  type CutOffTone,
} from '../../shared/cutOffDisplay';
import { Icon } from '../Icon/Icon';
import styles from './CutOffPill.module.css';

/** The cut-off status, structurally the basket client's union (without `serverNow`). */
export type CutOffPillStatus = CutOffDisplayStatus;

export interface CutOffPillCountdown {
  readonly secondsLeft: number;
  readonly tone: CutOffTone;
}

export type CutOffPillSize = 'compact' | 'small' | 'large';

export interface CutOffPillProps {
  readonly status: CutOffPillStatus | null;
  readonly countdown: CutOffPillCountdown | null;
  readonly size?: CutOffPillSize;
  /** The size below 768px, when it differs from `size`. */
  readonly phoneSize?: CutOffPillSize;
  /** Pulse in the final seconds (default `true`). */
  readonly enableFinalSecondsPulse?: boolean;
}

/** The reserved E2E hook (`TestIds.cutOffCountdown` in apps/web-e2e). */
export const CUT_OFF_COUNTDOWN_TEST_ID = 'cut-off-countdown';

const PASSED_COPY = 'Cut-off passed';
/** The clock's size per pill size (Figma Inspect). The phone rule resizes it in CSS. */
const ICON_PX: Readonly<Record<CutOffPillSize, number>> = { compact: 10, small: 12, large: 16 };
/** The pill pulses from this many seconds left down to zero. */
const PULSE_FROM_SECONDS = 10;
const ANNOUNCE_AT_MINUTES = [60, 30, 10] as const;

/** What the live region should say for a move from `previous` to `current` seconds left. */
function announcementFor(previous: number, current: number): string | null {
  if (previous > 0 && current <= 0) return PASSED_COPY;
  for (const minutes of [...ANNOUNCE_AT_MINUTES].reverse()) {
    const threshold = minutes * 60;
    if (previous > threshold && current <= threshold) return `Cut-off in ${minutes} minutes`;
  }
  return null;
}

export interface CutOffAnnouncerProps {
  readonly status: CutOffPillStatus | null;
  readonly countdown: CutOffPillCountdown | null;
}

interface AnnouncerReading {
  /** The deadline being counted to (`countdownDeadline`), so a new one starts afresh. */
  readonly deadline: string;
  readonly secondsLeft: number;
}

interface Announcement {
  readonly text: string;
  /** Bumped on every announcement, so the same text is rendered (and spoken) again. */
  readonly id: number;
}

export function CutOffAnnouncer({ status, countdown }: CutOffAnnouncerProps) {
  const deadline = countdown === null ? null : countdownDeadline(status);
  const secondsLeft = deadline === null || countdown === null ? null : countdown.secondsLeft;
  const previous = useRef<AnnouncerReading | null>(
    deadline === null || secondsLeft === null ? null : { deadline, secondsLeft },
  );
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  useEffect(() => {
    const before = previous.current;
    const current = deadline === null || secondsLeft === null ? null : { deadline, secondsLeft };
    previous.current = current;
    // A different deadline (a depot switch, or the refetch moving on to the next cut-off)
    // is a new countdown, not a crossing: nothing is said for the jump.
    if (before === null || current === null || before.deadline !== current.deadline) return;
    const next = announcementFor(before.secondsLeft, current.secondsLeft);
    if (next !== null) setAnnouncement((shown) => ({ text: next, id: (shown?.id ?? 0) + 1 }));
  }, [deadline, secondsLeft]);
  return (
    <span className={styles.srOnly} aria-live="polite" data-testid="cut-off-announcer">
      {announcement === null ? null : <span key={announcement.id}>{announcement.text}</span>}
    </span>
  );
}

export function CutOffPill({
  status,
  countdown,
  size = 'small',
  phoneSize,
  enableFinalSecondsPulse = true,
}: CutOffPillProps) {
  if (!isCutOffShown(status, countdown) || countdown === null) return null;
  const pulsing = enableFinalSecondsPulse && countdown.secondsLeft <= PULSE_FROM_SECONDS;

  return (
    <span
      className={styles.pill}
      data-size={size}
      data-phone-size={phoneSize}
      data-tone={countdown.tone}
      data-pulse={pulsing ? 'true' : undefined}
      role="timer"
      aria-label={countdownLabel(countdown.secondsLeft)}
      data-testid={CUT_OFF_COUNTDOWN_TEST_ID}
    >
      <Icon name="clock" size={ICON_PX[size]} />
      <span className={styles.text} aria-hidden>
        {formatCountdown(countdown.secondsLeft)}
      </span>
    </span>
  );
}
